import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  useSensors,
  useSensor,
  PointerSensor,
  closestCenter,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from "@dnd-kit/core";
import { useToast } from "@/hooks/use-toast";
import { createClient } from "@/lib/supabase/client";
import useMenuStore from "@/store/menuStore";
import { StructuredMenuProps } from "./types";
import { MenuItem, MenuCategory, Menu } from "@/types/menu";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu as MenuIcon, Pencil } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import NewMenuBanner from "./NewMenuBanner";
import CategoryNav from "./CategoryNav";
import CategorySection from "./CategorySection";
import MenuItemCard from "./MenuItemCard";
import MenuTitleEditDialog from "./MenuTitleEditDialog";
import LinkifyText from "@/components/ui/linkify-text";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/mixpanel-client";

// Define RTL languages
const RTL_LANGUAGES = ["arabic", "persian", "hebrew", "urdu"];

// Function to check if a language is RTL
function isRTLLanguage(language: string): boolean {
  return RTL_LANGUAGES.includes(language);
}

export function StructuredMenu({
  menu,
  menuId,
  language: initialLanguage,
  languageControls,
  customControls,
  isEditable = false,
  uiLabels,
  onBannerClick,
  supabase: externalSupabase,
}: StructuredMenuProps) {
  const { toast } = useToast();
  const t = useTranslations("StructuredMenu");
  const setMenu = useMenuStore((state) => state.setMenu);
  const updateCategory = useMenuStore((state) => state.updateCategory);
  const removeCategory = useMenuStore((state) => state.removeCategory);
  const saveToSupabase = useMenuStore((state) => state.saveToSupabase);
  const storeMenu: Menu | null = useMenuStore((state) => state.menu);
  const supabase = externalSupabase || createClient();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState<MenuItem | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [activeDropTarget, setActiveDropTarget] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const [overId, setOverId] = useState<string | undefined>(undefined);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [previousLanguage, setPreviousLanguage] = useState<string>("");
  const [isTitleDialogOpen, setIsTitleDialogOpen] = useState(false);

  // Track language changes
  useEffect(() => {
    if (initialLanguage && initialLanguage !== previousLanguage) {
      setPreviousLanguage(initialLanguage);
    }
  }, [initialLanguage, previousLanguage]);

  // Initialize store with menu prop
  useEffect(() => {
    if (menu && (!storeMenu || storeMenu.id !== menu.id)) {
      setMenu(menu);
    }
  }, [menu, setMenu, storeMenu]);

  const handleCategoryUpdate = async (updatedCategory: MenuCategory) => {
    updateCategory(updatedCategory, supabase);
    try {
      await saveToSupabase(menuId, supabase);
    } catch (error) {
      console.error("Error updating category:", error);
      toast({
        variant: "destructive",
        title: t("errors.categoryUpdateTitle"),
        description: t("errors.categoryUpdateMessage"),
      });
    }
  };

  const handleCategoryDelete = async (categoryToDelete: MenuCategory) => {
    removeCategory(categoryToDelete.id, supabase);
    try {
      await saveToSupabase(menuId, supabase);
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        variant: "destructive",
        title: t("errors.categoryDeleteTitle"),
        description: t("errors.categoryDeleteMessage"),
      });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "-64px 0px -80% 0px",
      threshold: 0,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveCategory(entry.target.id);
          // Scroll active category into view in the horizontal navigation
          const categoryButton = document.querySelector(
            `[data-category-id="${entry.target.id}"]`
          );
          const scrollContainer = document.querySelector(".category-scroll");
          if (categoryButton && scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const buttonRect = categoryButton.getBoundingClientRect();
            const browseButton = document.querySelector(".sticky.left-0");
            const browseButtonWidth =
              browseButton?.getBoundingClientRect().width || 0;

            // Calculate the scroll position to center the button
            const scrollLeft =
              buttonRect.left -
              containerRect.left -
              containerRect.width / 2 +
              buttonRect.width / 2 +
              browseButtonWidth;

            scrollContainer.scrollTo({
              left: scrollContainer.scrollLeft + scrollLeft,
              behavior: "smooth",
            });
          }
        }
      });
    }, options);

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => {
      observerRef.current?.observe(section);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [menu.categories]);

  const scrollToCategory = (categoryId: string) => {
    const element = document.getElementById(categoryId);
    if (element) {
      const header = document.querySelector("header");
      const mobileNav = document.querySelector(".mobile-nav");
      const headerHeight = (header as HTMLElement)?.offsetHeight || 0;
      const navHeight = (mobileNav as HTMLElement)?.offsetHeight || 0;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight - navHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setIsSheetOpen(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    if (event.active.data.current?.type === "item") {
      setDraggedItem(event.active.data.current.item);
      setActiveId(event.active.id as string);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (!over) {
      setActiveDropTarget(null);
      setOverId(undefined);
      return;
    }

    // Set the appropriate overId - could be an item or a category
    setOverId(over.id as string);

    // Check for category drop target
    const categoryId = over.data.current?.categoryId;
    if (categoryId) {
      setActiveDropTarget(categoryId);

      // If the over.data type is 'category', then we're hovering directly over a category
      // and not an item - set the overId to the category ID
      if (over.data.current?.type === "category") {
        setOverId(categoryId);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Reset drag states regardless of drop outcome
    setActiveDropTarget(null);
    setDraggedItem(null);
    setActiveId(undefined);
    setOverId(undefined);

    if (!over) return;

    if (active.id !== over.id && storeMenu) {
      // First create the updated menu
      const updatedMenu = reorderItems(
        storeMenu,
        active.id.toString(),
        over.id.toString()
      );

      // Then call setMenu with the updated menu
      setMenu(updatedMenu);

      // Save to Supabase after reordering
      saveToSupabase(menuId, supabase).catch((error) => {
        console.error("Error saving menu after reordering:", error);
      });
    }
  };

  // Helper function to reorder items in the menu
  const reorderItems = (menu: Menu, activeId: string, overId: string): Menu => {
    const clonedMenu = { ...menu };

    // Check if we're dropping directly onto a category (for empty categories)
    const isDirectCategoryDrop = clonedMenu.categories.some(
      (cat) => cat.id === overId
    );

    // Find which categories contain the active and over items
    let activeCategory: MenuCategory | null = null;
    let overCategory: MenuCategory | null = null;
    let activeItemIndex = -1;
    let overItemIndex = -1;

    // Find the active and over items in the menu categories
    for (const category of clonedMenu.categories) {
      // If dropping directly into a category
      if (category.id === overId && isDirectCategoryDrop) {
        overCategory = category;
        // Don't set overItemIndex here - this will make it add to the end of category
      }

      for (let i = 0; i < category.items.length; i++) {
        if (category.items[i].id === activeId) {
          activeCategory = category;
          activeItemIndex = i;
        }
        if (category.items[i].id === overId) {
          overCategory = category;
          overItemIndex = i;
        }
      }
    }

    // If we found both the active and over items in the same category
    if (
      activeCategory &&
      overCategory &&
      activeCategory.id === overCategory.id &&
      activeItemIndex !== -1 &&
      overItemIndex !== -1
    ) {
      // Reorder within the same category
      const items = [...activeCategory.items];
      const [movedItem] = items.splice(activeItemIndex, 1);
      items.splice(overItemIndex, 0, movedItem);

      // Update the category items
      const updatedCategories = clonedMenu.categories.map((category) =>
        category.id === activeCategory?.id ? { ...category, items } : category
      );

      return { ...clonedMenu, categories: updatedCategories };
    }

    // If moving between categories (more complex case)
    if (
      activeCategory &&
      overCategory &&
      activeCategory.id !== overCategory.id &&
      activeItemIndex !== -1
    ) {
      // Remove from source category
      const sourceItems = [...activeCategory.items];
      const [movedItem] = sourceItems.splice(activeItemIndex, 1);

      // Update the moved item's category reference
      movedItem.category = overCategory.id;

      // Add to destination category
      const destItems = [...overCategory.items];
      if (overItemIndex !== -1) {
        destItems.splice(overItemIndex, 0, movedItem);
      } else {
        destItems.push(movedItem);
      }

      // Update both categories
      const updatedCategories = clonedMenu.categories.map((category) => {
        if (category.id === activeCategory?.id) {
          return { ...category, items: sourceItems };
        }
        if (category.id === overCategory?.id) {
          return { ...category, items: destItems };
        }
        return category;
      });

      return { ...clonedMenu, categories: updatedCategories };
    }

    // If no changes could be made, return the original menu
    return menu;
  };

  const handleAddCategory = () => {
    if (!storeMenu) return;

    // Create default names for all supported languages
    const defaultNames = storeMenu.supportedLanguages.reduce((acc, lang) => {
      let defaultName = "New Category";
      switch (lang) {
        case "japanese":
          defaultName = "新しいカテゴリー";
          break;
        case "chinese_simplified":
          defaultName = "新类别";
          break;
        case "chinese_traditional":
          defaultName = "新類別";
          break;
        case "cantonese":
          defaultName = "新類別"; // Using Traditional Chinese characters as it's common in Cantonese
          break;
        case "korean":
          defaultName = "새 카테고리";
          break;
        case "thai":
          defaultName = "หมวดหมู่ใหม่";
          break;
        case "vietnamese":
          defaultName = "Danh mục mới";
          break;
        case "indonesian":
          defaultName = "Kategori Baru";
          break;
        case "malay":
          defaultName = "Kategori Baharu";
          break;
        case "filipino":
          defaultName = "Bagong Kategorya";
          break;
        case "french":
          defaultName = "Nouvelle catégorie";
          break;
        case "german":
          defaultName = "Neue Kategorie";
          break;
        case "italian":
          defaultName = "Nuova categoria";
          break;
        case "spanish":
          defaultName = "Nueva categoría";
          break;
        case "russian":
          defaultName = "Новая категория";
          break;
        case "hindi":
          defaultName = "नई श्रेणी";
          break;
        default:
          defaultName = "New Category";
      }
      return { ...acc, [lang]: defaultName };
    }, {});

    const newCategory: MenuCategory = {
      id: `category-${Date.now()}`,
      name: defaultNames,
      items: [],
    };

    const updatedMenu: Menu = {
      ...storeMenu,
      categories: [...storeMenu.categories, newCategory],
    };
    setMenu(updatedMenu);

    // Track Category Added event
    const primaryLanguage = storeMenu.supportedLanguages[0];
    trackEvent("Category Added", {
      "Menu ID": menuId, // menuId is from component props
      "Category Name":
        newCategory.name[primaryLanguage] ||
        Object.values(newCategory.name)[0] ||
        "", // Fallback
    });

    saveToSupabase(menuId, supabase).catch((error) => {
      console.error("Error adding category:", error);
      toast({
        variant: "destructive",
        title: t("errors.addCategoryTitle"),
        description: t("errors.addCategoryMessage"),
      });
      // Rollback on error
      setMenu(storeMenu);
    });
  };

  const handleTitleSave = async (updatedMenu: Menu) => {
    if (!storeMenu) return;

    setMenu(updatedMenu);
    try {
      await saveToSupabase(menuId, supabase);
      return Promise.resolve();
    } catch (error) {
      console.error("Error updating menu information:", error);
      toast({
        variant: "destructive",
        title: t("errors.updateMenuTitle"),
        description: t("errors.updateMenuMessage"),
      });
      setMenu(storeMenu);
      return Promise.reject(error);
    }
  };

  // Get the default language from supported languages
  const defaultLanguage = menu.supportedLanguages[0];
  // Use a function to get content in current language, falling back to previous language or default
  const getLocalizedContent = (
    contentObj: Record<string, string> = {},
    field: string = ""
  ) => {
    // Try current language first
    if (initialLanguage && contentObj[initialLanguage]) {
      return contentObj[initialLanguage];
    }

    // Then try previous language if available
    if (previousLanguage && contentObj[previousLanguage]) {
      return contentObj[previousLanguage];
    }

    // Fall back to default language
    if (contentObj[defaultLanguage]) {
      return contentObj[defaultLanguage];
    }

    // Last resort: any available language
    const firstAvailable = Object.values(contentObj)[0];
    return firstAvailable || (field ? field : "");
  };

  const effectiveLanguage = initialLanguage || defaultLanguage;

  // Check if current language is RTL
  const isRTL = isRTLLanguage(effectiveLanguage);

  // Handle banner click
  const handleLocalBannerClick = (index: number) => {
    if (onBannerClick) {
      onBannerClick(index);
    }
  };

  if (!storeMenu) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={isEditable ? handleDragStart : undefined}
      onDragOver={isEditable ? handleDragOver : undefined}
      onDragEnd={isEditable ? handleDragEnd : undefined}
    >
      <div className="container relative px-0 py-0">
        <NewMenuBanner
          mediaAssets={menu.mediaAssets || []}
          defaultMediaId={menu.defaultMediaId || null}
          onClick={handleLocalBannerClick}
          menu={{
            ...menu, // Spread all properties with index signature
          }}
          menuId={menuId}
          supabase={supabase}
          isEditable={isEditable}
          uiLabels={uiLabels}
        />

        <div className="px-4 pt-2">
          <div className="flex flex-col lg:flex-row sm:items-centerlg:items-start lg:justify-between gap-4 lg:gap-6">
            <div
              className={`${
                isEditable
                  ? "text-center lg:text-left"
                  : isRTL
                  ? "text-right"
                  : "text-center lg:text-left"
              }`}
            >
              <h1 className="text-2xl font-bold">
                {getLocalizedContent(menu.title, "Menu")}
                {isEditable && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setIsTitleDialogOpen(true)}
                          className={`${
                            isRTL ? "mr-2" : "ml-2"
                          } inline-flex items-center justify-center w-7 h-7 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors`}
                          aria-label="Edit menu title"
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("editMenuDetails")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </h1>
              {isEditable && !menu.title?.[effectiveLanguage] && (
                <div className="mt-1 text-sm text-blue-600">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setIsTitleDialogOpen(true)}
                          className="flex items-center gap-1 hover:underline"
                        >
                          <Pencil className="h-3 w-3" aria-hidden="true" />
                          {t("setMenuTitle")}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("addMenuTitle")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
              {menu.description && getLocalizedContent(menu.description) && (
                <LinkifyText
                  text={getLocalizedContent(menu.description)}
                  className={`text-muted-foreground mb-2 text-sm ${
                    isEditable
                      ? "text-center lg:text-left"
                      : isRTL
                      ? "text-right"
                      : "text-center lg:text-left"
                  }`}
                />
              )}
              <div
                className={`flex items-center ${
                  isEditable
                    ? "justify-center lg:justify-start"
                    : isRTL
                    ? ""
                    : "justify-center lg:justify-start"
                } gap-2 text-sm text-muted-foreground mt-1`}
              >
                <span>
                  {menu.categories.length}{" "}
                  {uiLabels?.categories || t("categories")}
                </span>
                <span aria-hidden="true">•</span>
                <span>
                  {menu.categories.reduce(
                    (acc, cat) => acc + (cat.items?.length || 0),
                    0
                  )}{" "}
                  {uiLabels?.items || t("items")}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2 w-full lg:w-auto">
              {languageControls}
              {customControls}
            </div>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-[320px,1fr] lg:gap-1 relative">
          {/* Desktop Sidebar */}
          <aside
            className="hidden lg:block sticky top-[var(--top-offset,64px)] h-[calc(100vh-var(--top-offset,64px))] "
            aria-label="Menu Categories Navigation"
          >
            <div className="h-full overflow-y-auto py-2">
              <div className="overflow-x-auto scrollbar-hide lg:overflow-visible">
                <div className="flex items-center w-full border-b lg:border-b-0 border-black/10">
                  <CategoryNav
                    menu={storeMenu}
                    language={effectiveLanguage}
                    activeCategory={activeCategory}
                    onCategoryClick={scrollToCategory}
                    onAddCategory={handleAddCategory}
                    isEditable={isEditable}
                  />
                </div>
              </div>
            </div>
          </aside>

          {/* Mobile Navigation */}
          <nav
            className="lg:hidden sticky top-[var(--top-offset,64px)] z-20 bg-background border-y mobile-nav"
            aria-label="Mobile Menu Categories"
          >
            <div className="flex">
              {/* Sticky Browse Button - Adjusted sticky positioning and border for RTL */}
              <div
                className={cn(
                  "sticky bg-background/95 backdrop-blur-sm z-10",
                  isRTL ? "right-0 border-l pl-4" : "left-0 border-r"
                )}
              >
                <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                  <SheetTrigger asChild>
                    <button
                      className={cn(
                        "whitespace-nowrap h-[43px] w-[43px] transition-colors flex items-center justify-center text-muted-foreground hover:text-foreground"
                      )}
                      aria-label={t("browse")}
                    >
                      <MenuIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    side="bottom"
                    className={cn(
                      "h-[85vh] rounded-t-xl px-0 flex flex-col",
                      // Add RTL specific styles for close button positioning
                      isRTL &&
                        "rtl [&>[data-radix-close-button]]:right-auto [&>[data-radix-close-button]]:left-4 [&>button]:right-auto [&>button]:left-4"
                    )}
                    // Set dir attribute for better RTL support within the sheet
                    dir={isRTL ? "rtl" : "ltr"}
                  >
                    <div className="px-4 pb-1 border-b">
                      <SheetTitle
                        className={cn(
                          "text-xl font-bold",
                          isRTL && "text-right"
                        )}
                      >
                        {getLocalizedContent(storeMenu.title, "Menu")}
                      </SheetTitle>
                      {storeMenu.description &&
                        getLocalizedContent(storeMenu.description) && (
                          <LinkifyText
                            text={getLocalizedContent(storeMenu.description)}
                            className={cn(
                              "text-muted-foreground mb-6 text-sm",
                              isRTL && "text-right"
                            )}
                          />
                        )}
                      {languageControls && (
                        <div
                          className={cn("mt-2", isRTL && "flex justify-end")}
                        >
                          {languageControls}
                        </div>
                      )}
                    </div>
                    <nav
                      className="flex-1 overflow-y-auto"
                      aria-label="Menu Categories"
                    >
                      <div className="px-4 py-1">
                        <CategoryNav
                          menu={storeMenu}
                          language={effectiveLanguage}
                          activeCategory={activeCategory}
                          onCategoryClick={(id) => {
                            scrollToCategory(id);
                            setIsSheetOpen(false);
                          }}
                          onAddCategory={handleAddCategory}
                          variant="sheet"
                          isEditable={isEditable}
                          // Pass isRTL prop
                          isRTL={isRTL}
                        />
                      </div>
                    </nav>
                  </SheetContent>
                </Sheet>
              </div>
              {/* Category Scroll Container - No changes needed here, should fill remaining space */}
              <div
                className="overflow-x-auto scrollbar-hide category-scroll"
                role="navigation"
                aria-label="Category Navigation"
              >
                <ul className={cn("flex min-w-max")}>
                  {storeMenu.categories.map((category) => {
                    const categoryId = `category-${category.id}`;
                    return (
                      <li key={category.id}>
                        <button
                          onClick={() => scrollToCategory(categoryId)}
                          className={cn(
                            "whitespace-nowrap px-4 py-2.5 text-sm transition-colors border-b-[3px]",
                            activeCategory === categoryId
                              ? "border-primary font-semibold text-primary dark:text-primary"
                              : "border-transparent text-muted-foreground hover:text-primary/90 dark:hover:text-primary/90 hover:border-primary/50"
                          )}
                          data-category-id={categoryId}
                          aria-current={
                            activeCategory === categoryId ? "true" : "false"
                          }
                        >
                          {getLocalizedContent(category.name)}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </nav>

          <main
            className="space-y-0 lg:pr-8 py-6 px-4 lg:px-0"
            aria-label="Menu Content"
          >
            {storeMenu.categories.map((category) => (
              <CategorySection
                key={category.id}
                category={{
                  ...category,
                  // Ensure category name is available in the current language
                  name: {
                    ...category.name,
                    [effectiveLanguage]: getLocalizedContent(category.name),
                  },
                }}
                language={effectiveLanguage}
                id={`category-${category.id}`}
                onUpdate={handleCategoryUpdate}
                onDelete={handleCategoryDelete}
                items={category.items
                  .filter(
                    (item) =>
                      // Include items that have content in any of our fallback languages
                      item.name[effectiveLanguage]?.trim() ||
                      (previousLanguage &&
                        item.name[previousLanguage]?.trim()) ||
                      item.name[defaultLanguage]?.trim()
                  )
                  .map((item) => ({
                    ...item,
                    // Ensure item name is available in the current language
                    name: {
                      ...item.name,
                      [effectiveLanguage]: getLocalizedContent(item.name),
                    },
                    // Ensure item description is available in the current language if it exists
                    description: item.description
                      ? {
                          ...item.description,
                          [effectiveLanguage]: getLocalizedContent(
                            item.description
                          ),
                        }
                      : undefined,
                  }))}
                isDropTarget={activeDropTarget === category.id}
                activeId={activeId}
                overId={overId}
                isEditable={isEditable}
                menu={menu}
                menuId={menuId}
              />
            ))}
          </main>
        </div>
      </div>

      <MenuTitleEditDialog
        menu={menu}
        language={effectiveLanguage}
        isOpen={isTitleDialogOpen}
        onClose={() => setIsTitleDialogOpen(false)}
        onUpdate={handleTitleSave}
        supportedLanguages={menu.supportedLanguages}
      />

      <DragOverlay>
        {draggedItem && (
          <div className="w-full opacity-90 rotate-2 scale-105 shadow-xl">
            <MenuItemCard
              item={draggedItem}
              language={effectiveLanguage}
              isEditable={isEditable}
              menu={menu}
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default StructuredMenu;
